import { SettingStorage } from '@services/SettingStorage';
import { SurveyStatsFiltersProps, SurveyStatsStatus } from '../types';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { DataPeriods } from '@utils/dataPeriods';

const STORAGE_KEY = 'admin-survey-filters';

export const getDefaultFilters = (initiativeId?: string): SurveyStatsFiltersProps => {
  return {
    months: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'],
    years: [new Date().getFullYear().toString()],
    periods: [DataPeriods.Monthly, DataPeriods.Quarterly, DataPeriods.Yearly],
    initiatives: initiativeId ? [initiativeId] : [],
    surveyStatuses: [SurveyStatsStatus.Completed, SurveyStatsStatus.Pending],
  };
};

export type FilterKey = keyof SurveyStatsFiltersProps;
// satisfies makes a filter added to SurveyStatsFiltersProps a compile error until listed here
const FILTER_KEY_SET = { months: true, years: true, periods: true, initiatives: true, surveyStatuses: true } satisfies Record<FilterKey, true>;
const FILTER_KEYS = Object.keys(FILTER_KEY_SET) as FilterKey[];

const isSameSelection = (a: string[] = [], b: string[] = []) => {
  const left = [...a].sort();
  const right = [...b].sort();
  return left.length === right.length && left.every((value, index) => value === right[index]);
};

/** Filters whose selection differs, ignoring order */
export const getChangedFilters = (current: SurveyStatsFiltersProps, saved: SurveyStatsFiltersProps): FilterKey[] =>
  FILTER_KEYS.filter((key) => !isSameSelection(current[key], saved[key]));

// An empty array is kept: the API reads it as no filter, i.e. all
const resolveFilterValues = <T extends string>(parsedFilter: unknown, fallbackValues?: T[]) => {
  return Array.isArray(parsedFilter) ? parsedFilter as T[] : fallbackValues;
};

/** A missing filter falls back to its default */
const normaliseFilters = (filters: SurveyStatsFiltersProps, initiativeId: string): SurveyStatsFiltersProps => {
  const defaults = getDefaultFilters(initiativeId);
  return {
    years: resolveFilterValues(filters.years, defaults.years),
    months: resolveFilterValues(filters.months, defaults.months),
    periods: resolveFilterValues(filters.periods, defaults.periods),
    initiatives: resolveFilterValues(filters.initiatives, defaults.initiatives),
    surveyStatuses: resolveFilterValues(filters.surveyStatuses, defaults.surveyStatuses),
  } satisfies Record<FilterKey, unknown>;
};
/**
 * Load saved filters from localStorage for the current user
 */
export const loadSavedFilters = ({
  userId,
  initiativeId,
}: {
  userId: string;
  initiativeId: string;
}): SurveyStatsFiltersProps => {
  // getItem throws where the browser blocks storage
  try {
    const stored = SettingStorage.getItem(`${STORAGE_KEY}-${userId}-${initiativeId}`);
    if (!stored) {
      return getDefaultFilters(initiativeId);
    }
    return normaliseFilters(JSON.parse(stored), initiativeId);
  } catch (error) {
    console.error('Failed to load saved filters', error);
    return getDefaultFilters(initiativeId);
  }
};

/** Returns whether the write succeeded; toasts either way */
export const saveFilters = ({
  userId,
  initiativeId,
  filters,
}: {
  userId: string;
  initiativeId: string;
  filters: SurveyStatsFiltersProps;
}): boolean => {
  try {
    SettingStorage.setItem(`${STORAGE_KEY}-${userId}-${initiativeId}`, JSON.stringify(filters));
    generateToast({
      color: 'success',
      message: 'Filters saved successfully',
    });
    return true;
  } catch (error) {
    toastFromError({ error, title: 'Failed to save filters' });
    return false;
  }
};
